import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIconBrand } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { t } from 'ember-intl';
import type { Hotel, Ota } from 'frontend-utils/models';
import type { Day, Period } from 'lumen/models';
import type { PeriodParity } from 'lumen/models/period';
import { filterMajorOTAs } from 'lumen/utils/parities-filter';
import { type BaseRate, formatBaseRate } from 'lumen/utils/parity-utils';

export interface TdPosBaserateColumn {
  availableOTAs: Ota[];
}

interface ParityTablesCellsTdPosBaserateSignature {
  Args: {
    cellData: Period;
    column: TdPosBaserateColumn;
  };
}

export default class ParityTablesCellsTdPosBaserate extends Component<ParityTablesCellsTdPosBaserateSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service declare rates: Services['rates'];

  // value is the parities object from the model (loop over all the parities to find the base rate)
  get baseRateObject(): PeriodParity | null {
    let baseRateObject: PeriodParity | null = null;

    if (this.period.parities) {
      this.period.parities.some((parityDataPerOta) => {
        if (parityDataPerOta.is_baserate) {
          baseRateObject = parityDataPerOta;
        }

        return parityDataPerOta.is_baserate;
      });
    }

    return baseRateObject;
  }

  get baseRates(): BaseRate[] {
    const { cellData, column } = this.args;

    const otaIds = column.availableOTAs?.map((ota) => ota.id);

    const baseRates = filterMajorOTAs(
      otaIds,
      // @ts-expect-error: Argument of type 'ParityDict | null' is not assignable to parameter of type 'ParityDict'.
      cellData.paritiesPOSDict,
      (parity) => parity.is_baserate,
    );

    return baseRates.map((baseRate) =>
      formatBaseRate(this.ota, this.rates, this.hotel, baseRate),
    );
  }

  get baseRateValue(): string {
    if (this.baseRateObject) {
      if (this.baseRateObject?.message) {
        return this.intl.t(this.baseRateObject?.message);
      }

      return `${this.rates.formatRateObject(this.baseRateObject, false)} ${
        this.baseRateObject?.currency
      }`;
    }

    return this.intl.t('general.not_available_short');
  }

  get day(): Day {
    return this.period.day;
  }

  get firstBaseRate(): BaseRate | undefined {
    return this.baseRates[0];
  }

  get hotel(): Hotel | null {
    return this.hotelAndMarketService.hotel;
  }

  get isToday(): boolean {
    return this.period.day.isToday;
  }

  get period(): Period {
    return this.args.cellData;
  }

  // quick admin only way to check API responses in the console
  @action
  onDoubleClick(): void {
    if (this.access.isStaffUser && console) {
      console.group(
        `Debugging info: ${this.day?.date.format('ddd l')} - parities objects`,
      );
      Array.prototype.forEach.call(
        this.period.paritiesPOSDict,
        (parityDataPerOta) => {
          console.log(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            `-- ${parityDataPerOta.ota} - ${parityDataPerOta.channel} --`,
          );
          console.dir(parityDataPerOta);
        },
      );

      console.groupEnd();
    }
  }

  <template>
    <div
      class="flex items-center justify-end tabular-nums
        {{if this.isToday 'td-today'}}"
    >
      <span
        class="parity-meet"
        role="tooltip"
        {{on "dblclick" this.onDoubleClick}}
      >
        {{this.baseRateValue}}
        {{#if this.baseRateObject}}
          <EmberTooltip
            @delay={{25}}
            @side="bottom"
            @tooltipClass="base-tooltip table-pos-tooltip"
          >
            <table cellspacing="0">
              <thead>
                <tr>
                  <th>{{t "parity.channel"}}</th>
                  <th>{{t "parity.lowest_rate"}}</th>
                  <th>{{t "parity.source"}}</th>
                  <th>{{t "general.update"}}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div class="flex items-center gap-100">
                      {{#each this.baseRates as |baseRate|}}
                        <PrismIconBrand @name={{baseRate.channel}} />
                      {{/each}}
                    </div>
                  </td>
                  <td>{{this.firstBaseRate.rateUnconverted}}</td>
                  <td>{{this.firstBaseRate.source}}</td>
                  <td>{{this.firstBaseRate.extractDatetimeFromNow}}</td>
                </tr>
              </tbody>
            </table>
          </EmberTooltip>
        {{/if}}
      </span>
    </div>
  </template>
}
